import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PanelLeftOpen } from 'lucide-react';
import UtmMap from '@/components/utm/map/UtmMap';
import UtmSidebar from '@/components/utm/map/UtmSidebar';
import UtmSelectionToolbar from '@/components/utm/map/UtmSelectionToolbar';
import UtmSelectionResultsPanel from '@/components/utm/map/UtmSelectionResultsPanel';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';
import { useFormResponseStore } from '@/store';

const FlightPlanSelectorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { sectionId, questionId } = useParams();
  const [searchParams] = useSearchParams();
  const [isDesktopSidebarHidden, setIsDesktopSidebarHidden] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { currentDrawingGeometry, selectionMode } = useUtmTaxonomyStore();
  const { setResponse } = useFormResponseStore();

  const sectionName = searchParams.get('sectionName') || '';
  const label = searchParams.get('label') || t('questionDesign.flightPlanDefaultLabel');
  const required = searchParams.get('required') === 'yes';
  const initialSelection = useMemo(() => {
    const candidate = (location.state as any)?.flightPlanSelection;
    if (!candidate?.geometry) {
      return null;
    }

    return {
      geometry: candidate.geometry as GeoJSON.Geometry,
      selectionMode: candidate.selectionMode as
        | 'polygon'
        | 'radius'
        | 'corridor'
        | undefined,
    };
  }, [location.state]);

  const canUseSelection = useMemo(() => {
    return Boolean(currentDrawingGeometry && sectionId && questionId);
  }, [currentDrawingGeometry, sectionId, questionId]);

  const goBackToAnswer = () => {
    navigate(-1);
  };

  const handleUseSelection = () => {
    if (!sectionId || !questionId || !currentDrawingGeometry) {
      toast.error(t('forms.selectFlightPlanBeforeUse'));
      return;
    }

    setResponse(sectionId, sectionName, questionId, 'Flight Plan', label, {
      geometry: currentDrawingGeometry,
      selectionMode:
        selectionMode === 'none' ? undefined : selectionMode,
    });

    navigate(-1);
  };

  return (
    <div className="-m-2 xs:-m-3 sm:-m-4 lg:-mx-8 h-[calc(100vh-5rem)] flex flex-col overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div>
          <h1 className="text-base font-semibold text-[#001A55]">{label}</h1>
          <p className="text-sm text-slate-600">
            {t('forms.flightPlanSelectorHelp')}
            {required ? ` (${t('forms.isRequired')})` : ''}
          </p>
        </div>

        <div className="flex w-full items-center gap-2 sm:w-auto">
          <button
            type="button"
            onClick={goBackToAnswer}
            className="w-1/2 rounded border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 sm:w-auto"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleUseSelection}
            disabled={!canUseSelection}
            className="w-1/2 rounded bg-darkBlue px-3 py-2 text-sm text-white hover:bg-darkBlue/90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {t('forms.useSelection')}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {!isMobile && (
          <UtmSidebar
            hidden={isDesktopSidebarHidden}
            onToggleHidden={() => setIsDesktopSidebarHidden((prev) => !prev)}
          />
        )}
        {isMobile && (
          <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
            <SheetContent side="left" className="w-[88vw] max-w-[22rem] p-0">
              <UtmSidebar hidden={false} showHideToggle={false} className="max-w-none" />
            </SheetContent>
          </Sheet>
        )}
        <div className="flex-1 min-h-0 relative overflow-hidden">
          {(!isMobile && isDesktopSidebarHidden) || (isMobile && !isMobileSidebarOpen) ? (
            <button
              type="button"
              onClick={() => {
                if (isMobile) {
                  setIsMobileSidebarOpen(true);
                  return;
                }
                setIsDesktopSidebarHidden(false);
              }}
              className="absolute left-3 top-3 z-30 inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs font-semibold text-[#3a3a3a] shadow-sm transition-colors hover:bg-[#f6f6f6] sm:px-4 sm:text-sm"
              aria-label="Show sidebar"
            >
              <PanelLeftOpen size={18} />
              MyMaps
            </button>
          ) : null}
          <UtmMap
            initialSelection={initialSelection}
            locationSearchLeftPx={isMobile ? 8 : isDesktopSidebarHidden ? 170 : 16}
            sidebarHidden={!isMobile && isDesktopSidebarHidden}
          />
          <UtmSelectionResultsPanel />
          <UtmSelectionToolbar />
        </div>
      </div>
    </div>
  );
};

export default FlightPlanSelectorPage;
